<template>
    <div>
      <ImageProcessing 
        ref="logo-background-removal"
        :logoUrl="logoUrl"
        :logoId="currentLogo ? currentLogo.id : -1"
        :ratioThreshold="ratioThreshold"
        :key="imgKey"
        @blobLoaded="onBlobLoaded"
        @canvasmounted="initialProcessImage"/>

        <div>
          <div>
            <LoadingSpinner v-if="loading"/>
            <div>
                <transition name="pop" appear mode="out-in">
                    <div>
                        <div v-show="!showFileUploader">
                            <LogoCarousel 
                            ref="logo-carousel"
                            v-show="logoHistory.length" 
                            :logoHistory="logoHistory" 
                            :tempLogoUrl="tempLogoUrl"
                            @slideChanged="onSlideChanged" />

                            <div v-if="!logoHistory.length && !loading">
                                {{ showFileUploader = true }}
                            </div>
                        </div>

                        <div v-if="showFileUploader" style="margin-left: 1rem; margin-right: 1rem;">
                            <FileUploader
                            ref="file-uploader"
                            showsize
                            :file-size-max="10"
                            :maxFiles="1"
                            @fileUploaded="onFileUpload" />
                        </div>
                    </div>
                </transition>
            </div>
          </div>

          <div v-if="logoHistory.length" style="margin: 1rem;">
            <GekkoButton 
              v-if="currentLogo && !loading && !showFileUploader"
              :text="$gettext('delete')"
              variant="link"
              class="text-danger"
              v-gk-confirm="{
              title: $gettext('Delete permanently?'),
              message: $gettext('Are you sure you want to delete this logo?'),
              options: {
                    variant: 'danger',
                    confirmText: $gettext('Delete')
                },
                callback: deleteLogo
              }" />
            
              <div v-if="!loading && !showFileUploader" style="display: flex; flex-direction: column; align-items: center;">
                <h3 style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 550px;">{{ logoHistory[$refs['logo-carousel'].slide].name }}</h3>
                <div>
                  <i>{{ logoHistory[$refs['logo-carousel'].slide].created | formatDate }}</i>
                </div>
              </div>

              <div v-if="!loading">
                <b-form-group label-class="gk-label-margin gk-modal-label">
                  <b-form-checkbox v-model="showFileUploader" switch>
                      {{ $gettext('Upload new image') }}
                  </b-form-checkbox>
                </b-form-group>
              </div>

              <div class="logo-actions" v-if="!loading && !showFileUploader">
                  <b-form-group label-class="gk-label-margin gk-modal-label" style="width: 20%;">
                      <template slot="label">
                          <span v-translate>Background</span>
                      </template>
                      <div style="display: flex; flex-direction: column; align-items: flex-start;">
                          <div style="display: flex; flex-direction: row; margin-bottom: 0.5rem;">
                              <b-form-checkbox v-model="isBackgroundRemoved" @change="removeBackground" />
                              <span v-translate>Remove</span>
                          </div>
                      </div>
                  </b-form-group>

                  <b-form-group label-class="gk-label-margin gk-modal-label" style="width: 80%;">
                      <template slot="label">
                          <span v-translate>Size</span>
                      </template>
                      <b-form-input v-model="ratioThreshold" type="range" min="0" max="1" step="0.05" @input="zoomChange" style="padding-right: 1rem; padding-left: 1rem;"></b-form-input>
                  </b-form-group>
              </div>

              <div v-if="currentLogo && !loading && !showFileUploader" class="logo-actions">
                <div v-if="!currentLogo.logobotrequest" style="display: flex; flex-direction: row;">
                  <div id="tooltip-elaborate">
                      <GekkoButton 
                      @click="editLogo('sketch')"
                      :text="$gettext('Elaborate')"
                      icon="fa-solid fa-paint-brush"
                      variant="outline-primary"/>
                  </div>
                  <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-elaborate" triggers="hover" container="#report-container">
                      <div class="tooltip-text" v-html="$gettext('Create a logo from the uploaded image. Ideal for pictures with clearly recognizable elements. <span style=\'color: white; font-weight: bold; text-decoration: underline;\'>Remove the background for a clean inclusion in your invoice.</span>')" />
                  </b-tooltip>

                  <div style="margin-left: 1rem;" id="tooltip-detail">
                      <GekkoButton 
                      @click="editLogo('structure')"
                      :text="$gettext('Detail')"
                      icon="fa-solid fa-eye"
                      variant="outline-primary"/>
                  </div>
                  <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-detail" triggers="hover" container="#report-container">
                      <div class="tooltip-text" v-html="$gettext('Create a drawing from the uploaded image, perfect for profile pictures. <span style=\'color: white; font-weight: bold; text-decoration: underline;\'>Remove the background for a clean inclusion in your invoice.</span>')" />
                  </b-tooltip>
                </div>

                <div>
                  <GekkoButton 
                    @click="setAsCompanyLogo"
                    :text="$gettext('Set as company logo')"
                    icon="fa-solid fa-check"/>
                </div>
              </div>
          </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { debounce } from '@/utils/'
import GekkoButton from '@/components/GekkoButton'
import ImageProcessing from '@/components/logo_generator/ImageProcessing.vue'
import CompanyService from '@/store/services/company.service.js'
import LoadingSpinner from '@/components/dashboard/ui/LoadingSpinner'
import AiAssistantService from '@/store/services/ai.assistant.service.js'
import LogoCarousel from '@/components/logo_generator/LogoCarousel.vue'
import FileUploader from '@/components/ui/FileUploader'

export default {
  name: 'EditLogo',
  components: {
    GekkoButton,
    ImageProcessing,
    LoadingSpinner,
    LogoCarousel,
    FileUploader
  },
  data () {
    return {
      loading: true,
      ratioThreshold: '0',
      isBackgroundRemoved: false,
      logoBlob: null,
      logoHistory: [],
      currentLogo: null,
      logoUrl: null,
      showFileUploader: false,
      tempLogoUrl: null,
      imgKey: 0,
      debouncedResize: null
    }
  },
  computed: {
    ...mapGetters ({
      currentCompany: 'main/getCurrentCompany',
      currentUser: 'main/getCurrentUser',
      isPremium: 'getIsPremium'
    }),
    logoBorder() { // TODO
      if (this.isBackgroundRemoved) return 'logo-border-background-removed'
      else return ''
    }
  },
  created () {
    AiAssistantService.getLogos().then((response) => {
      this.logoHistory = response.data
      if (this.logoHistory.length) {
        this.currentLogo = this.logoHistory[0]
        this.logoUrl = this.logoHistory[0].image
      }
      this.loading = false
    })

    this.debouncedResize = debounce(() => {
      this.$refs['logo-background-removal'].processImage(this.isBackgroundRemoved, this.ratioThreshold)
    }, 100)
  },
  methods: {
    close () {
      this.$emit('close', false)
      document.body.classList.remove('modal-on')
    },
    initialProcessImage () {
      this.$refs['logo-background-removal'].processImage(this.isBackgroundRemoved, this.ratioThreshold)
    },
    onBlobLoaded (blob) {
      this.logoBlob = blob
      this.logoUrl = URL.createObjectURL(blob)
      this.tempLogoUrl = this.logoUrl
      this.loading = false
    },
    resetConfig() {
      this.isBackgroundRemoved = false
      this.ratioThreshold = '0'
      this.logoUrl = this.currentLogo.image
    },
    undo () {
      this.resetConfig()
      this.tempLogoUrl = null
      this.$refs['logo-background-removal'].processImage(this.isBackgroundRemoved, this.ratioThreshold)
    },
    onSlideChanged(value) {
      if (value !== null) this.currentLogo = this.logoHistory[value]
      this.resetConfig()
      this.tempLogoUrl = this.logoUrl
      if (value !== null) this.$refs['logo-carousel'].updateSlide(null)
      else {
        this.imgKey++
        this.$refs['logo-background-removal'].processImage(this.isBackgroundRemoved, this.ratioThreshold)
      }
    },
    zoomChange() {
      this.debouncedResize()
    },
    removeBackground() {
      if (this.currentLogo.image_bg) {
        this.$refs['logo-background-removal'].processImage(this.isBackgroundRemoved, this.ratioThreshold)
      } else {
        this.loading = true
        AiAssistantService.logoRemoveBackground(this.currentLogo.id).then((response) => {
          this.currentLogo = response.data
          this.$refs['logo-background-removal'].processImage(this.isBackgroundRemoved, this.ratioThreshold)
        }).catch((response) => {
          if (response.status === 403) {
            this.$store.commit('togglePremiumModal', 'web_ai_assistant')
            this.loading = false
          } else {
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: this.$gettext('We could not process your logo'),
              variant: "danger",
              duration: 4
            })
          }
        })
      }
    },
    onFileUpload(file) {
      if (file.length) {
        this.loading = true
        this.$refs['logo-background-removal'].resizeImage(file[0]).then((imageBlob) => {
          let newLogoData = {
            name: file[0].name,
            image: new File([imageBlob], 'logo_image.png', { type: imageBlob.type }),
            user: this.currentUser.id
          }

          AiAssistantService.uploadNewLogo(newLogoData).then((response) => {
            if (response.status === 201) {
              this.logoHistory.unshift(response.data)
              this.$refs['logo-carousel'].updateSlide(0)
              this.onSlideChanged(0)
              this.showFileUploader = false
              this.loading = false
            }
          })
        })
      } else {
        this.$store.commit('setAlert', {
          title: this.$gettext('Error'),
          text: this.$gettext('We could not process your uploaded logo'),
          variant: "danger",
          duration: 4
        })
      }
    },
    deleteLogo() {
      if (!this.isPremium) {
        this.$store.commit('togglePremiumModal', 'web_ai_assistant')
      } else {
        AiAssistantService.deleteLogo(this.currentLogo.id).then((response) => {
          if (response.status === 204) {
            let index = this.logoHistory.findIndex(x => x.id === this.currentLogo.id)
            this.logoHistory.splice(index, 1)

            if (this.logoHistory.length - 1 <= index) index = 0
            
            this.$refs['logo-carousel'].updateSlide(index)
          }
        })
      }
    },
    editLogo(editType) {
      this.loading = true
      let tempId = this.currentLogo.id
      this.isBackgroundRemoved = false
      AiAssistantService.logoEditImage(tempId, {type: editType}).then((response) => {
        this.logoHistory.unshift(response.data)
        this.$refs['logo-carousel'].updateSlide(0)
        this.onSlideChanged(0)
        this.loading = false
      }).catch((response) => {
        if (response.status === 403) {
          this.$store.commit('togglePremiumModal', 'web_ai_assistant')
          this.loading = false
        } else {
          this.$store.commit('setAlert', {
            title: this.$gettext('Error'),
            text: this.$gettext('We could not process your logo'),
            variant: "danger",
            duration: 4
          })
        }
      })
    },
    async setAsCompanyLogo () {
      if (!this.isPremium) {
        this.$store.commit('togglePremiumModal', 'web_ai_assistant')
      } else {
        this.loading = true

        const response = await fetch(this.logoUrl)
        
        let file = null
        if (this.logoBlob !== null) {
          file = new File([this.logoBlob], 'logo_image.png', { type: this.logoBlob.type });
        } else {
          const blob = await response.blob();
          file = new File([blob], 'logo_image.jpg', { type: blob.type });
        }

        let companyData = {
          id: this.currentCompany.id,
          name: this.currentCompany.name,
          country: this.currentCompany.country,
          logo: file
        }

        CompanyService.updateLogo(this.currentCompany.id, companyData).then((response) => {
            this.$emit('logoSetToCompany', response.data)
            this.close()
            this.loading = false
        }).catch(() => {
            this.$store.commit('setAlert', {
                title: this.$gettext('Error'),
                text: this.$gettext('We could not update your company logo'),
                variant: "danger",
                duration: 4
            })
        })
      }
    }
  }
}
</script>

<style scoped>
.logo-actions {
  display: flex;
  justify-content: space-between;
}
</style>